'use client'

import 'react-responsive-carousel/lib/styles/carousel.min.css'

import { useEffect, useId, useMemo, useState } from 'react'
import { Carousel } from 'react-responsive-carousel'
import { useUiEventStore } from '@cerifi/state/ui-event.store'

import { Button } from '../../components/button'
import { IconButton } from '../../components/icon-button'
import { ChevronLeftIcon, ChevronRightIcon } from '../../components/icons'
import { ariaSpeak } from '../../utils/aria-speak'
import { cn } from '../../utils/cn'

interface BaseSliderProps {
  children: JSX.Element[]
  /** time between slides */
  interval?: number
  /** hex color value */
  color?: string
}

/** milliseconds before moving to next slide */
const DEFAULT_INTERVAL = 10_000

/** milliseconds of slide transition animation time */
const DEFAULT_ANIMATION_TIME = 1_000

export const BaseSlider = (props: BaseSliderProps) => {
  const id = useId()
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [a11yStopAnimation, setStopAnimation] = useState(false)

  /** Slider should not automatically move when a dialog or any other overlay that
   * blocks the rest of the site is open. (confusing, but also causes bugs)
   */
  const isOverlayOpen = useUiEventStore((state) => state.isOverlayOpen)

  const color = useMemo(() => props.color || '#333', [props.color])

  const onManualChange = (index: number, dir: 1 | -1) => {
    index += dir
    if (index >= props.children.length) {
      index = 0
    } else if (index < 0) {
      index = props.children.length - 1
    }
    ariaSpeak(`item ${index + 1} of ${props.children.length}`)
  }

  const a11yToggleAnimation = () => {
    ariaSpeak(
      `${!a11yStopAnimation ? 'animation stopped' : 'animation resumed'}`,
      'assertive'
    )
    setStopAnimation(!a11yStopAnimation)
  }

  const a11yMakeHiddenSlideUnFocusable = () => {
    const allSlides = document.getElementsByClassName(`slide-${id}`)

    /** The inner workings of the React Responsive Carousel will duplicate slides sometimes off the view
     * to assist with its animation. We want to treat those slides off the screen as not selected
     */
    function checkVisible(elm: Element) {
      var rect = elm.getBoundingClientRect()
      return !(rect.left < 0 || rect.left > window.innerWidth)
    }

    for (let i = 0; i < allSlides.length; i++) {
      const slide = allSlides.item(i) as Element
      const li = slide?.parentElement
      const inputs = slide?.querySelectorAll(
        'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
      )
      if (li?.classList.contains('selected') && checkVisible(slide)) {
        // un-hide all inputs on visible slide
        inputs?.forEach((i) => i.removeAttribute('tabindex'))
      } else {
        // hide all inputs on hidden slides
        inputs?.forEach((i) => i.setAttribute('tabindex', '-1'))
      }
    }
  }

  const onSlideChange = (index: number) => {
    setSelectedIndex(index)
    a11yMakeHiddenSlideUnFocusable()
  }

  useEffect(() => {
    /* sinful code to wait for the react-responsive-carousel to run its setup code.
     10 seems to work so I multiplied this time by 50 to make sure it runs... */
    setTimeout(() => {
      a11yMakeHiddenSlideUnFocusable()
    }, 500)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      className={cn(
        'base-slider relative -mt-12',
        props.children?.length > 10 && 'xl'
      )}
    >
      <div className="absolute left-3 top-3">
        <Button
          variant="outline"
          className="sr-hidden-until-focus z-10"
          onClick={() => a11yToggleAnimation()}
        >
          {a11yStopAnimation ? 'Resume' : 'Stop'} Animation
        </Button>
      </div>
      <Carousel
        infiniteLoop={true}
        autoPlay={a11yStopAnimation || isOverlayOpen ? false : true}
        showThumbs={false}
        stopOnHover={true}
        transitionTime={a11yStopAnimation ? 0 : DEFAULT_ANIMATION_TIME}
        interval={props.interval || DEFAULT_INTERVAL}
        emulateTouch={true}
        selectedItem={selectedIndex}
        onChange={(v) => onSlideChange(v)}
        renderArrowPrev={(
          clickHandler: () => void,
          hasPrev: boolean,
          label: string
        ) =>
          props.children.length > 1 && (
            <div
              className={cn(
                'absolute inset-y-0 left-0 z-10 hidden sm:flex lg:left-6',
                'items-center'
              )}
              onClick={() => {
                clickHandler()
                onManualChange(selectedIndex, -1)
              }}
            >
              <IconButton variant="ghost" aria-label="Previous Slide">
                <ChevronLeftIcon color={color} size={50} aria-hidden />
              </IconButton>
            </div>
          )
        }
        renderArrowNext={(
          clickHandler: () => void,
          hasPrev: boolean,
          label: string
        ) =>
          props.children.length > 1 && (
            <div
              className={cn(
                'inset-y-0 right-0 z-10 hidden sm:absolute sm:flex lg:right-6',
                'items-center'
              )}
              onClick={() => {
                clickHandler()
                onManualChange(selectedIndex, 1)
              }}
            >
              <IconButton variant="ghost" aria-label="Next Slide">
                <ChevronRightIcon color={color} size={50} aria-hidden />
              </IconButton>
            </div>
          )
        }
        renderItem={(
          item: React.ReactNode,
          options?: { isSelected: boolean; isPrevious: boolean }
        ) => {
          return (
            <div
              className={cn(
                props.children.length > 1 ? 'px-3' : '',
                'base-slider flex items-center h-full',
                `slide-${id}` // used for document selector
              )}
              id={`slide-${id}`}
              aria-hidden={!options?.isSelected}
            >
              {item}
            </div>
          )
        }}
        renderIndicator={(
          clickHandler: (e: React.MouseEvent | React.KeyboardEvent) => void,
          isSelected: boolean,
          index: number,
          label: string
        ) => {
          if (props.children?.length <= 1) return null

          return (
            <li>
              <button
                className={cn(
                  'rounded-full border-[8px] sm:border-[5px] focus:ring-8'
                )}
                style={{
                  color: color,
                  opacity: isSelected ? 1 : 0.3,
                }}
                onClick={($event) => {
                  clickHandler($event)
                }}
                aria-label={label + ' ' + index}
              />
            </li>
          )
        }}
      >
        {props.children}
      </Carousel>
    </div>
  )
}
